import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image,
    PDFViewer,
    Canvas,
} from "@react-pdf/renderer";
import Loading from "../../Common/Loading";

import logoAu from "./Image/md_logo_medium.png";
import logoUs from "./Image/md_logo_medium_us.png";
import footerLogoAu from "./Image/ke_logo_white.png";
import footerLogoUs from "./Image/aplus_america_logo_white.png";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const COLORS = {
    headerFooterBlue: "#468ecc",
    gapsPurple: "#6600cc",
    brickSolid: "#F9A31C",
    brickGap: "#d70000",
    brickSolidFaded: "#fbc776",
    brickGapFaded: "#e76666",
    areaOfStudyBlue: "#29649e"
};

const styles = StyleSheet.create({
    // FIX: measured the actual reference PDF pixel-by-pixel — the true
    // footer+disclaimer zone is only ~105pt tall, not 280pt. That 280pt
    // reservation was silently stealing ~175pt of usable content height
    // from every single page, which was the real reason the report ran
    // long and columns broke earlier than the reference.
    page: {
        paddingTop: 120,
        paddingBottom: 105,
        paddingLeft: 40,
        paddingRight: 40,
        fontFamily: "Helvetica",
        fontSize: 10,
        color: "#000",
    },
    header: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 110,
        backgroundColor: COLORS.headerFooterBlue,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingTop: 15,
        paddingLeft: 40,
        // paddingRight: 40,
    },
    headerTextContainer: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: "bold",
        color: "#fff",
    },
    headerSubtitle: {
        fontSize: 13,
        marginTop: 4,
        marginBottom: 4,
        color: "#fff",
    },
    headerMeta: {
        fontSize: 10,
        color: "#fff",
    },
    headerRightLogo: {
        width: 180,
        height: 65,
        objectFit: "contain",
    },

    footer: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: 105,
        paddingTop: 10,
        paddingBottom: 0,
        flexDirection: "column",
        justifyContent: "flex-end",
    },
    disclaimerText: {
        fontSize: 8,
        color: "#000",
        paddingLeft: 40,
        paddingRight: 40,
        marginBottom: 10,
        lineHeight: 1.3,
    },
    footerBlueBar: {
        backgroundColor: COLORS.headerFooterBlue,
        height: 60,
        width: "100%",
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "center",
        paddingRight: 40,
    },
    footerUrl: {
        color: "#fff",
        fontSize: 10,
    },
    brickwallSection: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 20,
    },
    gradeLabels: {
        width: "25%",
        textAlign: "right",
        paddingRight: 10,
        paddingTop: 15,
    },
    gradeLabelRow: {
        height: 25,
        justifyContent: "center",
        fontSize: 9,
    },
    canvasContainer: {
        width: "80%",
        paddingTop: 15,
    },
    improvementLabels: {
        width: "15%",
        paddingLeft: 10,
        paddingTop: 15,
    },
    improvementRow: {
        height: 25,
        justifyContent: "center",
        fontSize: 10,
        fontWeight: "bold",
        fontStyle: "italic",
    },
    legendRow: {
        flexDirection: "row",
        marginTop: 5,
    },
    progressBarContainer: {
        width: "100%",
        height: 15,
        backgroundColor: "#e0e0e0",
        border: "1px solid #ccc",
        marginTop: 2,
        position: "relative",
    },
    progressBarFill: {
        height: "100%",
        backgroundColor: "#5ec41c",
        position: "absolute",
        left: 0,
        top: 0,
    },
    progressNumbers: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 3,
        fontSize: 9,
        fontWeight: "bold",
    },
    table: {
        width: "100%",
    },
    tableRow: {
        flexDirection: "row",
    },
    tableCellHeader: {
        border: "1px solid #000",
        padding: 8,
        fontWeight: "bold",
        textAlign: "center",
        fontSize: 9,
    },
    tableCell: {
        border: "1px solid #000",
        padding: 8,
        textAlign: "center",
        fontSize: 9,
    },
    tableCellBlue: {
        backgroundColor: COLORS.areaOfStudyBlue,
        color: "#fff",
        border: "1px solid #000",
        padding: 8,
        textAlign: "center",
        fontSize: 10,
        fontWeight: "bold",
    },
    sectionTitle: {
        fontWeight: "bold",
        fontSize: 11,
        // marginTop: 2,
        // marginBottom: 5,
        textAlign: "left",
        // marginLeft:"118"
    },
    paragraph: {
        fontSize: 10,
        lineHeight: 1.4,
        color: "#58595B",
        marginBottom: 4,
        marginTop: "5"
    },
    // FIX: pixel-measured against the reference PDF (200dpi render). The
    // purple header box itself is ~20pt tall (10pt padding + ~10pt text
    // line), with ~10pt of clear space before it and ~10pt after — a 40pt
    // block in total. My previous guess compressed this too aggressively.
    gapHeader: {
        backgroundColor: COLORS.gapsPurple,
        padding: 5,
        marginBottom: 10,
        marginTop: 10,
    },
    gapHeaderText: {
        color: "#fff",
        fontSize: 11,
        fontWeight: "bold",
        // lineHeight: 1,
    },
    // FIX: alignItems 'flex-start' keeps the bullet glyph flush with the
    // FIRST line of wrapped text instead of stretching to the block's
    // center, which was the visual "gaps not aligned" issue.
    gapBullet: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 4,
    },
    bulletPoint: {
        width: 10,
        fontSize: 12,
        lineHeight: 1,
    },
    bulletText: {
        flex: 1,
        fontSize: 10,
        lineHeight: 1.3,
    },
    gapColumnsRow: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    gapColumn: {
        width: "48%",
        height: "95%"
    },
});

const stripHtml = (html: string) => {
    return html.replace(/<[^>]+>/g, "");
};

// Standard Helvetica (Core-14) character widths, per 1000 units of font size.
const HELVETICA_WIDTHS: Record<string, number> = {
    " ": 278, "!": 278, '"': 355, "#": 556, "$": 556, "%": 889, "&": 667, "'": 191,
    "(": 333, ")": 333, "*": 389, "+": 584, ",": 278, "-": 333, ".": 278, "/": 278,
    "0": 556, "1": 556, "2": 556, "3": 556, "4": 556, "5": 556, "6": 556, "7": 556, "8": 556, "9": 556,
    ":": 278, ";": 278, "<": 584, "=": 584, ">": 584, "?": 556, "@": 1015,
    A: 667, B: 667, C: 722, D: 722, E: 667, F: 611, G: 778, H: 722, I: 278, J: 500,
    K: 667, L: 556, M: 833, N: 722, O: 778, P: 667, Q: 778, R: 722, S: 667, T: 611,
    U: 722, V: 667, W: 944, X: 667, Y: 667, Z: 611,
    "[": 278, "\\": 278, "]": 278, "^": 469, _: 556, "`": 333,
    a: 556, b: 556, c: 500, d: 556, e: 556, f: 278, g: 556, h: 556, i: 222, j: 222,
    k: 500, l: 222, m: 833, n: 556, o: 556, p: 556, q: 556, r: 333, s: 500, t: 278,
    u: 556, v: 500, w: 722, x: 500, y: 500, z: 500,
    "{": 334, "|": 260, "}": 334, "~": 584,
};
const DEFAULT_CHAR_WIDTH = 556;
const BOLD_SCALE = 1.08; // approximation of Helvetica-Bold vs Helvetica width delta

function measureTextWidth(text: string, fontSize: number, bold = false): number {
    let units = 0;
    for (const ch of text) {
        units += HELVETICA_WIDTHS[ch] ?? DEFAULT_CHAR_WIDTH;
    }
    const width = (units / 1000) * fontSize;
    return bold ? width * BOLD_SCALE : width;
}

function countWrappedLines(text: string, maxWidth: number, fontSize: number, bold = false): number {
    const words = text.split(" ").filter(Boolean);
    if (words.length === 0) return 1;
    let lines = 1;
    let currentLine = "";
    for (const word of words) {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (measureTextWidth(testLine, fontSize, bold) <= maxWidth || !currentLine) {
            currentLine = testLine;
        } else {
            lines += 1;
            currentLine = word;
        }
    }
    return lines;
}

// Layout constants derived directly from the styles above (A4 = 595.28 x 841.89 pt)
const PAGE_CONTENT_WIDTH = 595.28 - 40 - 40; // 515.28

const PAGE_CONTENT_HEIGHT = 841.89 - 120 - 105; // 616.89


const PAGINATION_SAFETY_MARGIN = 20;

const COLUMN_WIDTH = PAGE_CONTENT_WIDTH * 0.48; // matches gapColumn width: "48%"
const BULLET_TEXT_WIDTH = COLUMN_WIDTH - 10; // minus bulletPoint width
const BULLET_FONT_SIZE = 9;
// Matches `bulletText` lineHeight: 1.3
const BULLET_LINE_HEIGHT = BULLET_FONT_SIZE * 1.3;
// Matches `gapBullet` marginBottom: 4
const BULLET_MARGIN_BOTTOM = 4;
// Matches `gapHeader`/`gapHeaderText`: padding 5 (top+bottom=10) +
// marginBottom 10 + marginTop 10 + text line (10 * 1)
const HEADER_BLOCK_HEIGHT = 10 + 10 + 10 + (10 * 1);

// NOTE: the reference PDF does not visibly render an intro sentence on the
// gap pages (verified against the rendered output), so there's no
// intro-height reservation to account for here.

type GapBlock =
    | { type: "header"; label: string }
    | { type: "item"; text: string };

interface GapColumn {
    blocks: GapBlock[];
}

interface GapPage {
    left: GapColumn;
    right: GapColumn;
}

function buildGapPages(
    gaps: Array<{ DisplayYear: number; Gaps: string }>,
    gradeLabel: string
): GapPage[] {

    if (!gaps?.length) return [];

    const pages: GapPage[] = [];

    let currentPage: GapPage = {
        left: { blocks: [] },
        right: { blocks: [] }
    };

    let currentColumn = currentPage.left;
    let currentHeight = 0;

    // FIX: subtract the safety margin so the packer stops itself slightly
    // before the true physical limit, instead of relying on React-PDF to
    // catch (and silently repaginate) any overflow.
    const MAX_HEIGHT = PAGE_CONTENT_HEIGHT - PAGINATION_SAFETY_MARGIN;

    const addPage = () => {
        pages.push(currentPage);

        currentPage = {
            left: { blocks: [] },
            right: { blocks: [] }
        };

        currentColumn = currentPage.left;
        currentHeight = 0;
    };

    const moveToNextColumn = () => {
        if (currentColumn === currentPage.left) {
            currentColumn = currentPage.right;
            currentHeight = 0;
            return;
        }

        addPage();
    };

    gaps.forEach(group => {

        const items = group.Gaps
            .split("/")
            .map(x => x.trim())
            .filter(Boolean);

        let firstHeader = true;

        items.forEach(item => {

            const headerHeight = HEADER_BLOCK_HEIGHT;

            const lines = countWrappedLines(
                item,
                BULLET_TEXT_WIDTH,
                BULLET_FONT_SIZE
            );

            const itemHeight =
                lines * BULLET_LINE_HEIGHT +
                BULLET_MARGIN_BOTTOM;

            if (firstHeader) {

                if (currentHeight + headerHeight > MAX_HEIGHT) {
                    moveToNextColumn();
                }

                currentColumn.blocks.push({
                    type: "header",
                    label: `${gradeLabel} ${group.DisplayYear} gaps:`
                });

                currentHeight += headerHeight;

                firstHeader = false;
            }

            if (currentHeight + itemHeight > MAX_HEIGHT) {

                moveToNextColumn();

                currentColumn.blocks.push({
                    type: "header",
                    label: `${gradeLabel} ${group.DisplayYear} gaps (cont.):`
                });

                currentHeight += headerHeight;
            }

            currentColumn.blocks.push({
                type: "item",
                text: item
            });

            currentHeight += itemHeight;
        });

    });

    if (
        currentPage.left.blocks.length ||
        currentPage.right.blocks.length
    ) {
        pages.push(currentPage);
    }

    return pages;
}
const EPDFReport: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<any>(null);

    const schoolCode = searchParams.get("SchoolCode") || "";
    const familyCode = searchParams.get("FamilyCode") || "";
    const loginId = searchParams.get("Login") || "";
    const reportNo = searchParams.get("ReportNo") || sessionStorage.getItem('ReportNo') || '';

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const query = new URLSearchParams({ SchoolCode: schoolCode, FamilyCode: familyCode, Login: loginId, ReportNo: reportNo });
                const res = await fetch(`${API_BASE_URL}api/get_still_report_EC?${query}`);
                const result = await res.json();
                if (result.status === "success") setData(result.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchReport();
    }, [schoolCode, familyCode, loginId, reportNo]);

    // Must run unconditionally (before any early return) per Rules of Hooks.
    const gapPages = useMemo(() => {
        if (!data) return [] as GapPage[];
        const gl = data.report_meta?.GradeLabel && data.report_meta.GradeLabel.toLowerCase().includes("year")
            ? "Grade"
            : data.report_meta?.GradeLabel || "Grade";
        return buildGapPages(data.gaps || [], gl);
    }, [data]);

    if (loading) return <Loading />;
    if (!data) return <div>Error loading report data.</div>;

    const { student_info, report_meta, brickwall, summary_text, recommendation_text, strand_performance } = data;
    const gradeLabel = report_meta?.GradeLabel && report_meta.GradeLabel.toLowerCase().includes("year") ? "Grade" : report_meta?.GradeLabel || "Grade";
    const showFutureNote = brickwall.some((b: any) => b.IsFutureYear);
    const brand = report_meta.IsUSOrCanada ? "English" : "English";

    const contactBrand = report_meta.IsUSOrCanada ? "A+ America" : "Kinetic Education";
    const contactPhone = report_meta.IsUSOrCanada ? "your local number" : "1800 262 847";

    const gradeScaleMax = report_meta.IsNZ ? 11 : 10;
    const gradeAchievedRaw = Number(report_meta.GradeAchieved);
    const gradeAchievedNum = Number.isFinite(gradeAchievedRaw)
        ? Math.max(0, Math.min(gradeScaleMax, gradeAchievedRaw))
        : 0;
    const gradeAchievedPercent = Math.max(2, (gradeAchievedNum / gradeScaleMax) * 100);



    const renderHeader = () => (
        <View style={styles.header} fixed>
            <View style={{ ...styles.headerTextContainer, paddingBottom: "-10" }}>
                <Text style={styles.headerTitle}>
                    {student_info.FName ? student_info.FName.trim() : ""} <Text style={{ fontWeight: 'normal' }}>{student_info.LName ? student_info.LName.trim() : ""}</Text>
                </Text>
                <Text style={styles.headerSubtitle}>English Report</Text>
                <Text style={{...styles.headerMeta, width:"120%"}}>
                    School Level: {gradeLabel} {report_meta.ActualGrade} / Assessment Completed on: {report_meta.TestCompletionDate}
                </Text>
            </View>
            <Image
                src={report_meta.IsUSOrCanada ? logoUs : logoAu}
                style={{ width: 250, height: 100, objectFit: "contain", paddingBottom: "10" }}
            />
        </View>
    );

    const renderFooter = () => (
        <View style={styles.footer} fixed>
            <Text style={styles.disclaimerText}>
                Disclaimer: {brand} Check® reports are to be used as a guideline only. No responsibility is taken if results provided by {brand} Check® do not translate to school reports or any other form of assessment. It is assumed that the user used {brand} Check® in accordance of the guidelines specified.
            </Text>
            <View style={[styles.footerBlueBar, { justifyContent: "space-between", paddingLeft: 40, paddingRight: 40 }]}>
                <Image
                    src={report_meta.IsUSOrCanada ? footerLogoUs : footerLogoAu}
                    style={{ width: 120, height: 40, objectFit: "contain" }}
                />
                <Text style={styles.footerUrl}>
                    {report_meta.IsUSOrCanada ? "www.aplusamerica.com" : "www.kineticeducation.com.au"}
                </Text>
            </View>
        </View>
    );

    const renderGapColumn = (col: GapColumn, keyPrefix: string) => (
        <View style={styles.gapColumn}>
            {col.blocks.map((block, i) =>
                block.type === "header" ? (
                    <View key={`${keyPrefix}-h-${i}`} style={styles.gapHeader}>
                        <Text style={styles.gapHeaderText}>{block.label}</Text>
                    </View>
                ) : (
                    <View key={`${keyPrefix}-i-${i}`} style={styles.gapBullet}>
                        <Text style={styles.bulletPoint}>•</Text>
                        <Text style={styles.bulletText}>{block.text}</Text>
                    </View>
                )
            )}
        </View>
    );

    const MyDocument = () => (
        <Document title={`${familyCode}_${student_info.FName ? student_info.FName.trim() : ""}_${reportNo}.pdf`}>
            <Page size="A4" style={styles.page} wrap>

                {renderHeader()}
                {renderFooter()}

                <Text style={{ ...styles.sectionTitle, marginLeft: "80px", marginBottom:"-5" }}>Results by Grade:</Text>
                <View style={styles.brickwallSection}>
                    <View style={styles.gradeLabels}>
                        {brickwall.map((b: any) => (
                            <View
                                key={`L${b.Year}`}
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginBottom: 8,
                                }}
                            >
                                <View style={{ width: 80 }}>
                                    <Text
                                        style={{
                                            backgroundColor: b.IsStudyYear ? "#dbedfb" : "transparent",
                                            padding: "3.3",
                                            color: "black",
                                            fontWeight: b.IsStudyYear ? "normal" : "normal",
                                            fontSize: "9px",
                                        }}
                                    >
                                        {b.YearLongForm?.toString().replace(/Year\b/i, "Grade")}
                                    </Text>
                                </View>
                            </View>
                        ))}
                    </View>

                    <View style={{ ...styles.canvasContainer, marginBottom: "-20", marginLeft: "-35px" }}>
                        <Canvas
                            style={{ width: '100%', height: brickwall.length * 25 }}
                            paint={(painter, width, height) => {
                                const brickHeight = 22;
                                const rowPitch = 24;
                                const brickW = 45;
                                const brickGapPx = 2;
                                const radius = 2;

                                brickwall.forEach((brick: any, i: number) => {
                                    const rowTop = i * rowPitch;
                                    const pGap = (100 - brick.ScoreAchieved) / 100;
                                    const gapWidth = pGap * width;
                                    const center = width / 2;
                                    const redStart = center - gapWidth / 2;
                                    const redEnd = center + gapWidth / 2;

                                    const solidColor = brick.IsFutureYear ? COLORS.brickSolidFaded : COLORS.brickSolid;
                                    const gapColor = brick.IsFutureYear ? COLORS.brickGapFaded : COLORS.brickGap;

                                    // Staggered "running bond" pattern per row, mirroring VB PaintBricks'
                                    // Even/Odd offset.
                                    const rowFromBottom = brickwall.length - i;
                                    const stagger = rowFromBottom % 2 !== 0 ? 0 : -(brickW / 2);

                                    let x = stagger;
                                    while (x < width) {
                                        const bx = Math.max(x, 0);
                                        const bxEnd = Math.min(x + brickW, width);
                                        const bw = bxEnd - bx - brickGapPx;

                                        if (bw > 0) {
                                            const brickCenter = bx + bw / 2;
                                            const isGapBrick = brickCenter > redStart && brickCenter < redEnd;

                                            painter
                                                .fillColor(isGapBrick ? gapColor : solidColor)
                                                .roundedRect(bx, rowTop, bw, brickHeight, radius)
                                                .fill();
                                        }
                                        x += brickW;
                                    }
                                });
                                return null;
                            }}
                        />
                        <View style={styles.legendRow}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', marginRight: 20 }}>
                                <View style={{ width: 15, height: 8, backgroundColor: COLORS.brickSolid, marginRight: 6, borderRadius: 2 }} />
                                <Text style={{ fontSize: 9 }}>Solid Understanding</Text>
                            </View>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <View style={{ width: 15, height: 8, backgroundColor: COLORS.brickGap, marginRight: 6, borderRadius: 2 }} />
                                <Text style={{ fontSize: 9 }}>Gaps in Understanding</Text>
                            </View>
                        </View>
                        {showFutureNote && (
                            <Text style={{ fontSize: 9, marginTop: 10, color: "#272727" }}>
                                * Note: Faded bricks represents future grades.
                            </Text>
                        )}
                    </View>

                    <View style={styles.improvementLabels}>
                        {brickwall.map((b: any) => (
                            <View key={`R${b.Year}`} style={styles.improvementRow}>
                                <Text style={{ color: b.ImprovementColor }}>{b.Improvement}</Text>
                            </View>
                        ))}
                    </View>
                </View>

                <View style={{ backgroundColor: "#F0F0F0", marginTop: 10, marginLeft: -110, marginRight: -40, paddingLeft: 40, paddingRight: 40, paddingTop: 12, paddingBottom: 12 }}>
                    <View style={{ marginLeft: 70 }}>
                        <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 10 }}>
                            Grade Levels Achieved:
                        </Text>
                        <View style={styles.progressNumbers}>
                            {(report_meta.IsNZ ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]).map((n) => (
                                <Text key={n}>{n}</Text>
                            ))}
                        </View>
                        <View style={styles.progressBarContainer}>
                            <View style={[styles.progressBarFill, { width: `${gradeAchievedPercent}%` }]}></View>
                        </View>
                    </View>

                    {strand_performance && strand_performance.length > 0 && (
                        <View style={{ marginTop: 20, marginLeft: 70 }}>
                            <Text style={styles.sectionTitle}>Grade Achievement by Area of Study:</Text>

                            <View style={{ flexDirection: "row", marginTop: 0 }}>

                                <View style={{ width: 30, justifyContent: "center", alignItems: "center" }}>
                                    <Text style={{
                                        transform: "rotate(-90deg)",
                                        fontSize: 9,
                                        fontWeight: "bold",
                                        width: 100,
                                        textAlign: "center",
                                        marginTop: -30
                                    }}>
                                        Achievement{"\n"}Grades
                                    </Text>
                                </View>

                                <View style={{ flex: 1 }}>

                                    <View style={{
                                        flexDirection: "row",
                                        borderLeft: "2px solid #000",
                                        borderBottom: "2px solid #000",
                                        paddingTop: 10,
                                        paddingBottom: 5,
                                        paddingLeft: 5,
                                        paddingRight: 5,
                                        justifyContent: "space-between"
                                    }}>
                                        {strand_performance.map((s: any, i: number) => (
                                            <View key={`block_${i}`} style={{
                                                flex: 1,
                                                backgroundColor: COLORS.areaOfStudyBlue,
                                                marginHorizontal: 3,
                                                paddingVertical: 18,
                                                justifyContent: "center",
                                                alignItems: "center"
                                            }}>
                                                <Text style={{ color: "#fff", fontSize: 18 }}>{s.Level}</Text>
                                            </View>
                                        ))}
                                    </View>

                                    <View style={{
                                        flexDirection: "row",
                                        paddingLeft: 5,
                                        paddingRight: 5,
                                        marginTop: 6,
                                        justifyContent: "space-between"
                                    }}>
                                        {strand_performance.map((s: any, i: number) => (
                                            <View key={`label_${i}`} style={{ flex: 1, marginHorizontal: 3, alignItems: "center" }}>
                                                <Text style={{ fontSize: 7, fontWeight: "bold", textAlign: "center" }}>
                                                    {s.StrandName}
                                                </Text>
                                            </View>
                                        ))}
                                    </View>

                                    <View style={{ marginTop: 15, alignItems: "center" }}>
                                        <Text style={{ fontSize: 10, fontWeight: "bold" }}>Areas of Study</Text>
                                    </View>

                                </View>
                            </View>
                        </View>
                    )}
                </View>

                <View>
                    <Text style={{ ...styles.sectionTitle, marginTop: "10" }}>Summary:</Text>

                    <Text style={styles.paragraph}>{stripHtml(summary_text)}</Text>
                    <Text style={styles.sectionTitle}>Recommendation:</Text>
                    <Text style={styles.paragraph}>{stripHtml(recommendation_text)}</Text>
                </View>

                <Text style={{ fontSize: 9, marginTop: 10 }}>
                    For more information regarding the reports or advice on a lesson plan, please contact{" "}
                    <Text style={{ fontWeight: "bold" }}>{contactBrand} on {contactPhone}.</Text>
                </Text>

            </Page>


            {gapPages
                .filter(
                    page =>
                        page.left.blocks.length ||
                        page.right.blocks.length
                )
                .map((page, pIdx) => (
                    <Page key={`gap-page-${pIdx}`} size="A4" style={styles.page} wrap={false}>
                        {renderHeader()}
                        {renderFooter()}

                        <View style={[styles.gapColumnsRow, { marginTop: 15 }]}>
                            {renderGapColumn(page.left, `p${pIdx}-l`)}
                            {renderGapColumn(page.right, `p${pIdx}-r`)}
                        </View>
                    </Page>
                ))}
        </Document>
    );

    const safeFName = student_info.FName ? student_info.FName.trim() : "Student";
    const fileName = `${familyCode}_${safeFName}_${reportNo}.pdf`;

    return (
        <PDFViewer
            style={{ width: "100%", height: "100vh", minHeight: "100dvh", border: "none", display: "block" } as any}
            {...{ title: fileName } as any}
        >
            <MyDocument />
        </PDFViewer>
    );
};

export default EPDFReport;